




import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { IResult, UUID } from "@/types";

export interface IUnityInstance {
  SendMessage: (
    object: string,
    functionName: string,
    argument: undefined | string | number
  ) => void;
  Quit: () => Promise<any>;
}

declare const createUnityInstance: Function;

const WEBGL_PATH = "/WebGL/Build";
const LOADER_URL = `${WEBGL_PATH}/WebGL.loader.js?v=a20059d4a3ae52b51d6cfb389e84cda6e0c5c302`;

@Component
export default class Visualization extends Vue {
  @Prop({ type: Boolean, default: true }) enabled!: boolean;

  // Dev tool to mock plant selection without using visualisation
  @Prop({ type: Boolean, default: false }) mock!: boolean;

  @Prop({ required: true }) data!: IResult | null;
  @Prop({ required: true }) layers!: number[];
  @Prop({ required: true }) systems!: string[];
  @Prop({ required: true }) crops!: string[];

  $refs!: {
    visualizationContainer: HTMLCanvasElement;
  };

  private instance?: IUnityInstance;
  private lastSelected: UUID | null = null;

  private readonly unityHandlers: { [key: string]: Function } = {
    ready: this.onReady,
    selected: this.onSelected,
    sceneInitialised: this.onSceneInitialised,
    print: (msg: any) => console.debug(`UNITY PRINT: ${msg}`),
    loadCustomResult: this.onLoadCustomResult,
  };

  public resetCamera() {
    this.sendMessage("ResetPosition", undefined, "CameraSystem");
  }

  @Watch("enabled")
  private onEnabledChange() {
    if (this.enabled) {
      this.initialize();
    }
  }

  public highlightAllSelected() {
    this.sendMessage("SelectAllInstancesByID", this.lastSelected || "");
  }

  private initialize() {
    // Dev tool to mock plant selection without using visualisation
    if (this.mock) {
      this.$emit("ready");
      this.$emit("initialised");
      this.$emit(
        "selected",
        this.data?.plantList[
          Math.floor(this.data?.plantList.length * Math.random())
        ].id
      );
      return;
    }

    if (this.instance) {
      console.debug("Unity visualization already initialized!");
      return;
    }

    (window as any).UnityReceiveMesaage = true; // to remove, workaround due to typo
    (window as any).UnityReceiveMessage = this.receiveMessage;

    const config = {
      dataUrl: `${WEBGL_PATH}/WebGL.data.gz?v=a20059d4a3ae52b51d6cfb389e84cda6e0c5c302`,
      frameworkUrl: `${WEBGL_PATH}/WebGL.framework.js.gz?v=a20059d4a3ae52b51d6cfb389e84cda6e0c5c302`,
      codeUrl: `${WEBGL_PATH}/WebGL.wasm.gz?v=a20059d4a3ae52b51d6cfb389e84cda6e0c5c302`,
    };

    const scriptEl = document.createElement("script");
    scriptEl.src = LOADER_URL;
    scriptEl.addEventListener("load", () => {
      createUnityInstance(
        this.$refs.visualizationContainer,
        config,
        (progress: number) => {
          this.$emit("updateProgress", progress);
        }
      ).then((unityInstance: IUnityInstance) => {
        this.instance = unityInstance;
        this.$emit("ready", this.instance);
        scriptEl.remove();
      });
    });
    document.head.appendChild(scriptEl);
  }

  private onReady() {
    this.sendMessage("LoadData", JSON.stringify(this.data));
  }

  private onSceneInitialised() {
    this.$emit("initialised");
  }

  private onSelected(plantId: string) {
    this.lastSelected = plantId;
    this.$emit("selected", plantId);
  }

  private onLoadCustomResult(result: string) {
    this.$emit("customResult", JSON.parse(result));
  }

  @Watch("layers")
  private updateLayerShow() {
    this.sendMessage("SelectLayers", this.layers.map(String).join(","));
  }

  @Watch("systems")
  private updateSystemsShow() {
    this.sendMessage("SetSystemTags", this.systems.join(","));
  }

  @Watch("crops")
  private updateCropsShow() {
    this.sendMessage("SetCropTags", this.crops.join(","));
  }

  private sendMessage(
    functionName: string,
    argument?: undefined | string | number,
    objectName = "Main"
  ) {
    if (this.instance) {
      console.debug(
        `UNITY SEND ${functionName}(${
          typeof argument === "string" ? `"${argument}"` : String(argument)
        }) -> "${objectName}"`
      );
      this.instance.SendMessage(objectName, functionName, argument);
    }
  }

  private receiveMessage(functionName: string, argument?: any) {
    console.debug(
      `UNITY RECV ${functionName}(${
        typeof argument === "string" ? `"${argument}"` : String(argument)
      })`
    );
    if (this.unityHandlers[functionName]) {
      this.unityHandlers[functionName](argument);
    } else {
      console.warn("No corresponding handler found!");
    }
  }
}
